import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Switch, 
  Select, 
  InputNumber, 
  message, 
  Tabs, 
  Typography,
  Row,
  Col,
  Divider,
  Alert,
  Space
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  SecurityScanOutlined,
  SettingOutlined,
  DatabaseOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { usePermission } from '../store/permission';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Settings: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { canUpdate } = usePermission();

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      // 模拟保存
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('设置保存成功');
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    message.info('设置已重置');
  };

  const systemSettings = [
    {
      key: 'general',
      label: '基本设置',
      icon: <SettingOutlined />,
      content: (
        <Form layout="vertical" form={form} onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="siteName"
                label="网站名称"
                rules={[{ required: true, message: '请输入网站名称' }]}
              >
                <Input placeholder="防护链接管理平台" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="siteDescription"
                label="网站描述"
              >
                <Input placeholder="专业的防护链接管理平台" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="adminEmail"
                label="管理员邮箱"
                rules={[
                  { required: true, message: '请输入管理员邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="admin@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="timezone"
                label="时区设置"
              >
                <Select placeholder="请选择时区">
                  <Option value="Asia/Shanghai">Asia/Shanghai (UTC+8)</Option>
                  <Option value="UTC">UTC (UTC+0)</Option>
                  <Option value="America/New_York">America/New_York (UTC-5)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="enableRegistration"
                label="开放注册"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="enableEmailVerification"
                label="邮箱验证"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )
    },
    {
      key: 'security',
      label: '安全设置',
      icon: <SecurityScanOutlined />,
      content: (
        <Form layout="vertical" form={form} onFinish={handleSave}>
          <Alert
            message="安全设置"
            description="这些设置影响系统的安全性，请谨慎修改"
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="passwordMinLength"
                label="密码最小长度"
                rules={[{ required: true, message: '请输入密码最小长度' }]}
              >
                <InputNumber min={6} max={20} placeholder="8" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="passwordExpireDays"
                label="密码过期天数"
              >
                <InputNumber min={0} max={365} placeholder="90" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxLoginAttempts"
                label="最大登录尝试次数"
                rules={[{ required: true, message: '请输入最大登录尝试次数' }]}
              >
                <InputNumber min={3} max={10} placeholder="5" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lockoutDuration"
                label="锁定时间（分钟）"
              >
                <InputNumber min={5} max={1440} placeholder="30" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="enableTwoFactor"
                label="启用双因素认证"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="enableSessionTimeout"
                label="启用会话超时"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="allowedIPs"
            label="允许的IP地址"
          >
            <TextArea 
              rows={3} 
              placeholder="每行一个IP地址，留空表示允许所有IP"
            />
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'database',
      label: '数据库设置',
      icon: <DatabaseOutlined />,
      content: (
        <Form layout="vertical" form={form} onFinish={handleSave}>
          <Alert
            message="数据库设置"
            description="数据库连接和性能相关设置"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dbHost"
                label="数据库主机"
                rules={[{ required: true, message: '请输入数据库主机' }]}
              >
                <Input placeholder="localhost" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dbPort"
                label="数据库端口"
                rules={[{ required: true, message: '请输入数据库端口' }]}
              >
                <InputNumber min={1} max={65535} placeholder="3306" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dbName"
                label="数据库名称"
                rules={[{ required: true, message: '请输入数据库名称' }]}
              >
                <Input placeholder="fanghu_db" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dbCharset"
                label="字符集"
              >
                <Select placeholder="请选择字符集">
                  <Option value="utf8mb4">utf8mb4</Option>
                  <Option value="utf8">utf8</Option>
                  <Option value="latin1">latin1</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxConnections"
                label="最大连接数"
                rules={[{ required: true, message: '请输入最大连接数' }]}
              >
                <InputNumber min={1} max={1000} placeholder="100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="connectionTimeout"
                label="连接超时（秒）"
              >
                <InputNumber min={1} max={60} placeholder="10" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dbLogLevel"
            label="数据库日志级别"
          >
            <Select placeholder="请选择日志级别">
              <Option value="error">Error</Option>
              <Option value="warn">Warn</Option>
              <Option value="info">Info</Option>
              <Option value="debug">Debug</Option>
            </Select>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'performance',
      label: '性能设置',
      icon: <GlobalOutlined />,
      content: (
        <Form layout="vertical" form={form} onFinish={handleSave}>
          <Alert
            message="性能设置"
            description="系统性能和缓存相关设置"
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cacheEnabled"
                label="启用缓存"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cacheExpire"
                label="缓存过期时间（秒）"
              >
                <InputNumber min={60} max={86400} placeholder="3600" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxUploadSize"
                label="最大上传大小（MB）"
                rules={[{ required: true, message: '请输入最大上传大小' }]}
              >
                <InputNumber min={1} max={100} placeholder="10" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="enableCompression"
                label="启用压缩"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="workerProcesses"
                label="工作进程数"
                rules={[{ required: true, message: '请输入工作进程数' }]}
              >
                <InputNumber min={1} max={16} placeholder="4" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="requestTimeout"
                label="请求超时（秒）"
              >
                <InputNumber min={5} max={300} placeholder="30" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="logLevel"
            label="日志级别"
          >
            <Select placeholder="请选择日志级别">
              <Option value="error">Error</Option>
              <Option value="warn">Warn</Option>
              <Option value="info">Info</Option>
              <Option value="debug">Debug</Option>
            </Select>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <div>
      <Title level={2}>系统设置</Title>
      
      <Card>
        <Tabs
          defaultActiveKey="general"
          items={systemSettings}
          tabPosition="left"
          style={{ minHeight: 500 }}
        />
        
        <Divider />
        
        <div style={{ textAlign: 'center' }}>
          <Space>
            <Button 
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={() => form.submit()}
              disabled={!canUpdate}
            >
              保存设置
            </Button>
            
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleReset}
            >
              重置设置
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default Settings; 